import { clampClipRatio } from './clampClipRatio';
import { computeFillerRegion } from './fillerPlaylist';
import { isValidFractionalRect, type FractionalRect } from './deriveClipPosition';

/** Where the satisfatorio title band sits: a horizontal band at `pos` (0–1 of
 *  the height) or a vertical seam at `pos` (0–1 of the width). */
export interface TitleSeam {
  axis: 'horizontal' | 'vertical';
  pos: number;
}

const EPS = 1e-9;

/** The clip/filler seam the title straddles. With a free-floating `clipRect`
 *  it is the cell edge shared with the filler strip (horizontal band for a
 *  strip above/below, vertical seam for a strip beside — regardless of the
 *  composition orientation). Without `clipRect` (or with a junk/degenerate
 *  one) it is the legacy division line, byte-identical to the historical
 *  inline math in SatisfatorioFrame. */
export function computeTitleSeam(
  config: { clipRatio?: number; clipPosition?: string; clipRect?: FractionalRect },
  isLandscape: boolean,
): TitleSeam {
  const rect = config.clipRect;
  if (isValidFractionalRect(rect)) {
    const strip = computeFillerRegion(rect, isLandscape);
    if (strip.w * strip.h > EPS) {
      if (strip.w >= 1 - EPS) {
        // Band above or below the cell → horizontal seam at the shared edge.
        const above = strip.y + strip.h <= rect.y + EPS;
        return { axis: 'horizontal', pos: above ? rect.y : rect.y + rect.h };
      }
      const left = strip.x + strip.w <= rect.x + EPS;
      return { axis: 'vertical', pos: left ? rect.x : rect.x + rect.w };
    }
  }
  const ratio = clampClipRatio(config.clipRatio);
  const clipFirst = config.clipPosition !== 'bottom';
  return {
    axis: isLandscape ? 'vertical' : 'horizontal',
    pos: (clipFirst ? ratio : 100 - ratio) / 100,
  };
}
